import Typography, { ETypo, ETypoColor } from "@Front/Components/DesignSystem/Typography";
import Dot from "@Front/Components/Layouts/Legal/LegalInformations/elements/Dot";
import { useDeploymentLineLegalPublisher } from "@Front/Services/DeploymentLineLegalPublisher/DeploymentLineLegalPublisherContext";
import LinkInline from "../../elements/LinkInline";
import { Routes, buildRoute } from "@Front/Config/Routes";
import { ELegalOptions } from "@Front/Components/LayoutTemplates/DefaultLegalDashboard";
import {
	resolvePublicWebDisplayHostForBrowserOrBuild,
	resolvePublicWebOriginForBrowserOrBuild,
} from "@Front/Utils/deploymentSitePublicWebClient";
import type { DeploymentLineLegalPublisher } from "le-coffre-resources/dist/deploymentLineLegalPublisher";

function CguSection1PartiesDot(props: { pub: DeploymentLineLegalPublisher }): React.JSX.Element {
	const { pub } = props;
	return (
		<Dot>
			« Parties » : désigne au singulier indistinctement {pub.publisherNameDefinitionSingular} ou l&apos;Internaute, et au pluriel{" "}
			{pub.partiesPluralUseLeArticle ? (
				<>
					le {pub.publisherLegalName} et l&apos;Internaute ensemble ;
				</>
			) : (
				<>
					{pub.publisherLegalName} et l&apos;Internaute ensemble ;
				</>
			)}
		</Dot>
	);
}

export function CGUSection1(): React.JSX.Element {
	const publicSiteOrigin = resolvePublicWebOriginForBrowserOrBuild();
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				1. Définitions
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les termes ci-dessous définis auront entre les Parties la signification suivante :
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				<Dot>« Internaute » : toute personne accédant et mettant en œuvre les fonctionnalités du Site ;</Dot>
				<CguSection1PartiesDot pub={pub} />
				<Dot>
					« Règlementation relative à la protection des données à caractère personnel » : ° la loi relative à l&apos;informatique, aux
					fichiers et aux libertés n° 78-17 du 6 janvier 1978 modifiée et ses mises à jour ou modification ; et
				</Dot>
				<Dot>
					le Règlement (UE) 2016/679 du Parlement européen et du Conseil du 27 avril 2016 (règlement général sur la protection des
					données) ;
				</Dot>
				<Dot>
					« Site » : site Internet édité par {pub.publisherLegalName} accessible à
					<LinkInline text={publicSiteOrigin} href={`${publicSiteOrigin}/`} />
				</Dot>
			</Typography>
		</section>
	);
}

export function CGUSection2(): React.JSX.Element {
	const publicSiteHost = resolvePublicWebDisplayHostForBrowserOrBuild();
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				2. Description des services
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Le Site permet un accès au service {pub.publisherLegalName} qui est une plateforme en ligne, par abonnement, permettant le transfert
				sécurisé de données et documents à destination des notaires et de leurs offices.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Le fait de déposer un document sur la plateforme entraîne donc la conservation temporaire des documents transmis.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Une fois le dossier complété et l&apos;ensemble des documents nécessaires à la préparation d&apos;un acte notarié réalisé, le notaire
				peut obtenir l&apos;ensemble des documents après que la plateforme {publicSiteHost} ait certifié l&apos;origine du document via
				l&apos;identification de son émetteur et verrouillé l&apos;état des documents par l&apos;établissement d&apos;un jeton unique
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				La technologie utilisée repose sur l&apos;utilisation d&apos;une technologie blockchain.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				L&apos;Internaute est invité à se référer aux
				<LinkInline
					text="Conditions Générales de Service"
					href={buildRoute(Routes.pages.Legal.LegalInformations, { legalUid: ELegalOptions.CGS })}
				/>
				concernant l&apos;utilisation de la plateforme.
			</Typography>
		</section>
	);
}

export function CGUSection3(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				3. Entrée en vigueur – Durée
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les présentes conditions générales d&apos;utilisation entrent en vigueur à la date de leur mise en ligne et seront opposables à
				la date de la première utilisation du Site par l&apos;Internaute.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les présentes conditions d&apos;utilisation sont opposables pendant toute la durée d&apos;utilisation du Site et jusqu&apos;à ce que de
				nouvelles conditions générales d&apos;utilisation remplacent les présentes.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				L&apos;Internaute peut à tout moment renoncer à utiliser le Site mais reste responsable de toute utilisation antérieure.
			</Typography>
		</section>
	);
}

export function CGUSection4(): React.JSX.Element {
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				4. Objet des CGU
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Les présentes CGU ont pour objet de présenter et de fixer les conditions d&apos;utilisation du Site, et l&apos;ensemble des pages du
				Site.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				L&apos;Internaute déclare avoir pris connaissance et accepté les présentes conditions générales d&apos;utilisation
			</Typography>
		</section>
	);
}

function CGUSection5Tail(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Toutefois, {pub.publisherLegalName} se réserve le droit, sans préavis, ni indemnité, de fermer temporairement ou définitivement tout ou
				partie du service notamment pour effectuer une mise à jour, des opérations de maintenance, des modifications ou changements
				sur les méthodes opérationnelles et les serveurs, sans que cette liste ne soit limitative.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				{pub.publisherLegalName} n&apos;est pas responsable des dommages de toute nature qui peuvent résulter de ces changements ou d&apos;une
				indisponibilité temporaire ou encore de la fermeture définitive de tout ou partie du Site ou des services qui y sont
				associés.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				{pub.publisherLegalName} se réserve le droit de compléter ou de modifier, à tout moment, le Site et les services qui y sont disponibles
				en fonction de l&apos;évolution des technologies.{" "}
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				Il appartient à l&apos;Internaute de veiller aux possibilités d&apos;évolution des moyens informatiques et de transmission à sa
				disposition pour que ces moyens puissent s&apos;adapter aux évolutions du service.
			</Typography>
		</>
	);
}

export function CGUSection5(): React.JSX.Element {
	const pub = useDeploymentLineLegalPublisher();
	return (
		<section>
			<Typography typo={ETypo.TITLE_H6} color={ETypoColor.TEXT_ACCENT}>
				5. Conditions d&apos;accès
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				L&apos;accès au Site est libre et gratuit pour tout Internaute disposant d&apos;un accès à internet ou à un réseau mobile. Tous les
				coûts afférents à l&apos;accès, que ce soit les frais matériels, logiciels ou d&apos;accès à internet sont exclusivement à la charge
				de l&apos;Utilisateur. Il est seul responsable du bon fonctionnement de son équipement mobile ainsi que de son accès à internet.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				{pub.publisherLegalName} s&apos;efforce de fournir un accès de qualité et de permettre aux Internautes d&apos;utiliser les moyens de communication
				mis à leur disposition dans les meilleures conditions possibles.
			</Typography>
			<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.TEXT_SECONDARY}>
				En raison de la nature et de la complexité du réseau de l&apos;Internet, et en particulier, de ses performances techniques et des
				temps de réponse pour consulter, interroger ou transférer les données d&apos;informations, {pub.publisherLegalName} fait ses meilleurs
				efforts, conformément aux règles de l&apos;art, pour permettre l&apos;accès et l&apos;utilisation du Site. {pub.publisherLegalName} ne saurait en effet
				assurer une accessibilité ou une disponibilité absolue du Site.
			</Typography>
			<CGUSection5Tail />
		</section>
	);
}
